import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  BookOpen, 
  Code, 
  Terminal, 
  Download, 
  ExternalLink,
  ChevronRight,
  Copy,
  CheckCircle,
  Users
} from 'lucide-react'

const TechnicalDocs: React.FC = () => {
  const [copiedCode, setCopiedCode] = useState('')

  const copyToClipboard = (code: string, id: string) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(id)
    setTimeout(() => setCopiedCode(''), 2000)
  }

  const sections = [
    {
      title: '快速开始',
      icon: BookOpen,
      content: [
        {
          title: '安装和配置',
          description: '了解如何安装和配置CoreTrust开发环境',
          code: `npm install @deeplink/coretrust-sdk
import { CoreTrustSDK } from '@deeplink/coretrust-sdk'

const sdk = new CoreTrustSDK({
  apiKey: 'your-api-key',
  network: 'mainnet'
})`
        },
        {
          title: '基础用法',
          description: '学习SDK的基本使用方法',
          code: `// 初始化钱包
const wallet = await sdk.createWallet()

// 连接现有钱包
const wallet = await sdk.connectWallet(privateKey)

// 发送交易
const tx = await wallet.sendTransaction({
  to: '0x...',
  value: '0.1',
  data: '0x...'
})`
        }
      ]
    },
    {
      title: 'API参考',
      icon: Code,
      content: [
        {
          title: '钱包API',
          description: '钱包相关的API接口文档',
          code: `interface Wallet {
  address: string
  balance: Promise<string>
  sendTransaction(tx: Transaction): Promise<string>
  signMessage(message: string): Promise<string>
  encrypt(data: string): Promise<string>
  decrypt(encryptedData: string): Promise<string>
}`
        },
        {
          title: '区块链API',
          description: '区块链交互相关的API',
          code: `interface BlockchainAPI {
  getBalance(address: string): Promise<string>
  getTransaction(txHash: string): Promise<Transaction>
  getBlock(blockNumber: number): Promise<Block>
  callContract(method: string, params: any[]): Promise<any>
}`
        }
      ]
    },
    {
      title: '示例代码',
      icon: Terminal,
      content: [
        {
          title: '创建DApp',
          description: '使用CoreTrust SDK创建去中心化应用',
          code: `import React from 'react'
import { CoreTrustProvider } from '@deeplink/coretrust-react'

function App() {
  return (
    <CoreTrustProvider>
      <YourDApp />
    </CoreTrustProvider>
  )
}`
        },
        {
          title: '集成Web3功能',
          description: '在现有应用中集成Web3功能',
          code: `// 检查钱包连接状态
const isConnected = await sdk.isWalletConnected()

// 监听账户变化
sdk.on('accountsChanged', (accounts) => {
  console.log('账户变化:', accounts)
})

// 监听网络变化
sdk.on('networkChanged', (networkId) => {
  console.log('网络变化:', networkId)
})`
        }
      ]
    }
  ]

  const resources = [
    {
      title: 'SDK下载',
      description: '下载最新版本的CoreTrust SDK',
      icon: Download,
      links: [
        { name: 'JavaScript SDK', url: 'https://github.com/deeplink-limited/coretrust-js-sdk', version: 'v1.2.0' },
        { name: 'React SDK', url: 'https://github.com/deeplink-limited/coretrust-react-sdk', version: 'v1.1.5' },
        { name: 'Vue SDK', url: 'https://github.com/deeplink-limited/coretrust-vue-sdk', version: 'v1.0.8' },
        { name: 'Python SDK', url: 'https://github.com/deeplink-limited/coretrust-python-sdk', version: 'v0.9.2' }
      ]
    },
    {
      title: '开发工具',
      description: '辅助开发的工具和资源',
      icon: ExternalLink,
      links: [
        { name: '开发者控制台', url: 'https://console.deeplink.hk', version: '在线' },
        { name: '测试网络', url: 'https://testnet.deeplink.hk', version: '免费' },
        { name: 'API Explorer', url: 'https://api.deeplink.hk/explorer', version: '交互式' },
        { name: '代码生成器', url: 'https://tools.deeplink.hk/generator', version: '自动' }
      ]
    },
    {
      title: '文档资源',
      description: '详细的技术文档和指南',
      icon: BookOpen,
      links: [
        { name: 'API参考文档', url: 'https://docs.deeplink.hk/api', version: '完整' },
        { name: '快速开始指南', url: 'https://docs.deeplink.hk/quickstart', version: '新手' },
        { name: '最佳实践', url: 'https://docs.deeplink.hk/best-practices', version: '高级' },
        { name: '故障排除', url: 'https://docs.deeplink.hk/troubleshooting', version: '帮助' }
      ]
    },
    {
      title: '社区资源',
      description: '开发者社区和支持',
      icon: Users,
      links: [
        { name: 'GitHub仓库', url: 'https://github.com/deeplink-limited', version: '开源' },
        { name: 'Discord社区', url: 'https://discord.gg/deeplink', version: '活跃' },
        { name: '技术论坛', url: 'https://forum.deeplink.hk', version: '讨论' },
        { name: '开发者博客', url: 'https://blog.deeplink.hk', version: '更新' }
      ]
    }
  ]

  return (
    <>
      <Helmet>
        <title>技术文档 - DeepLink Limited</title>
        <meta name="description" content="DeepLink Limited技术文档，包含API参考、SDK使用指南和开发示例。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-8">
                <BookOpen className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                技术文档
              </h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto">
                完整的API参考、SDK使用指南和开发示例
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    文档目录
                  </h3>
                  <nav className="space-y-2">
                    {sections.map((section, index) => (
                      <a
                        key={section.title}
                        href={`#${section.title.toLowerCase().replace(/\s+/g, '-')}`}
                        className="block px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      >
                        {section.title}
                      </a>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Main Content */}
              <div className="lg:col-span-3">
                {sections.map((section, sectionIndex) => {
                  const Icon = section.icon
                  return (
                    <motion.div
                      key={section.title}
                      id={section.title.toLowerCase().replace(/\s+/g, '-')}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
                      viewport={{ once: true }}
                      className="mb-16"
                    >
                      <div className="flex items-center mb-8">
                        <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mr-4">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                          {section.title}
                        </h2>
                      </div>

                      <div className="space-y-8">
                        {section.content.map((item, itemIndex) => (
                          <div key={itemIndex} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                              {item.title}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">
                              {item.description}
                            </p>
                            
                            <div className="relative">
                              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                                <pre className="text-green-400 text-sm">
                                  <code>{item.code}</code>
                                </pre>
                              </div>
                              <button
                                onClick={() => copyToClipboard(item.code, `${sectionIndex}-${itemIndex}`)}
                                className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                              >
                                {copiedCode === `${sectionIndex}-${itemIndex}` ? (
                                  <CheckCircle className="w-4 h-4 text-green-400" />
                                ) : (
                                  <Copy className="w-4 h-4 text-gray-300" />
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Resources */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                开发资源
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                下载SDK、使用工具和获取更多资源
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {resources.map((resource, index) => {
                const Icon = resource.icon
                return (
                  <motion.div
                    key={resource.title}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-8"
                  >
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mr-4">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                          {resource.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          {resource.description}
                        </p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {resource.links.map((link, linkIndex) => (
                        <a
                          key={linkIndex}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <div className="flex items-center">
                            <span className="text-gray-900 dark:text-white font-medium">
                              {link.name}
                            </span>
                            <span className="ml-2 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                              {link.version}
                            </span>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-400" />
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default TechnicalDocs